import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { ticketsService } from '../services/api-service';
import { QrCode, CheckCircle, XCircle, AlertCircle, Camera, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import './Scanner.css';

const Scanner = () => {
  const { user } = useAuth();
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [error, setError] = useState(null);
  const [cameraPermission, setCameraPermission] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [validating, setValidating] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const qrCodeReaderRef = useRef(null);

  useEffect(() => {
    // Check camera permission
    checkCameraPermission();
    
    return () => {
      stopCamera();
    };
  }, []);

  const checkCameraPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach(track => track.stop());
      setCameraPermission('granted');
    } catch (err) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setCameraPermission('denied');
      } else {
        setCameraPermission('error');
      }
    }
  };

  const startCamera = async () => {
    try {
      setError(null);
      setScanResult(null);
      setValidationResult(null);
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment' // Use back camera on mobile
        }
      });
      
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      
      setScanning(true);
      
      // Initialize QR code scanning
      // Note: In production, you would use a library like html5-qrcode or react-qr-reader
      // For now, we'll use a simple approach with manual QR code detection
      startQRCodeScanning();
      
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError('Failed to access camera. Please check permissions.');
      setCameraPermission('denied');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    
    setScanning(false);
    
    if (qrCodeReaderRef.current) {
      clearInterval(qrCodeReaderRef.current);
      qrCodeReaderRef.current = null;
    }
  };

  const startQRCodeScanning = () => {
    // Simple QR code scanning simulation
    // In production, use html5-qrcode library:
    // import { Html5Qrcode } from 'html5-qrcode';
    // const html5QrCode = new Html5Qrcode("scanner-container");
    // html5QrCode.start(
    //   { facingMode: "environment" },
    //   { fps: 10, qrbox: { width: 250, height: 250 } },
    //   onScanSuccess,
    //   onScanError
    // );
    
    // For now, we'll provide a manual input option
    console.log('QR code scanning ready. In production, use html5-qrcode library.');
  };

  const handleManualInput = async (qrData) => {
    if (!qrData || !qrData.trim()) {
      setError('Please enter a valid QR code data');
      return;
    }

    setValidating(true);
    setError(null);
    setValidationResult(null);
    setScanResult(qrData);

    try {
      // Extract ticket ID from QR code data
      // QR code format could be: "ticket:123" or just "123" or JSON
      let ticketId = qrData;
      
      // Try to parse if it's JSON
      try {
        const parsed = JSON.parse(qrData);
        ticketId = parsed.ticketId || parsed.id || qrData;
      } catch (e) {
        // Not JSON, try to extract from format like "ticket:123"
        if (qrData.includes(':')) {
          ticketId = qrData.split(':')[1];
        }
      }

      const result = await ticketsService.validateTicket({
        ticketId: ticketId,
        qrData: qrData,
        scannedAt: new Date().toISOString()
      });

      setValidationResult(result);
      
      // Stop camera after successful scan
      if (scanning) {
        stopCamera();
      }
      
    } catch (err) {
      console.error('Error validating ticket:', err);
      setError(err.message || 'Failed to validate ticket');
      setValidationResult({
        valid: false,
        error: err.message || 'Validation failed'
      });
    } finally {
      setValidating(false);
    }
  };

  const handleScanAgain = () => {
    setScanResult(null);
    setValidationResult(null);
    setError(null);
    if (!scanning) {
      startCamera();
    }
  };

  const getValidationMessage = () => {
    if (!validationResult) return null;

    if (validationResult.valid) {
      return {
        type: 'success',
        icon: <CheckCircle size={48} />,
        title: 'Ticket Valid',
        message: validationResult.message || 'This ticket is valid and has been scanned.',
        details: validationResult.ticket ? (
          <div className="scanner-ticket-details">
            <p><strong>Event:</strong> {validationResult.ticket.eventName}</p>
            <p><strong>Ticket #:</strong> {validationResult.ticket.ticketNumber}</p>
            <p><strong>Status:</strong> {validationResult.ticket.status}</p>
          </div>
        ) : null
      };
    } else if (validationResult.error === 'duplicate' || validationResult.duplicate) {
      return {
        type: 'warning',
        icon: <AlertCircle size={48} />,
        title: 'Ticket Already Used',
        message: 'This ticket has already been scanned.',
        details: validationResult.ticket ? (
          <div className="scanner-ticket-details">
            <p><strong>Scanned at:</strong> {new Date(validationResult.ticket.scannedAt).toLocaleString()}</p>
          </div>
        ) : null
      };
    } else if (validationResult.error === 'expired' || validationResult.expired) {
      return {
        type: 'warning',
        icon: <AlertCircle size={48} />,
        title: 'Ticket Expired',
        message: 'This ticket is for a past event.',
        details: null
      };
    } else {
      return {
        type: 'error',
        icon: <XCircle size={48} />,
        title: 'Invalid Ticket',
        message: validationResult.error || 'This ticket is not valid.',
        details: null
      };
    }
  };

  if (!user) {
    return (
      <div className="scanner-page">
        <div className="container">
          <div className="scanner-login-required">
            <QrCode size={64} />
            <h2>Login Required</h2>
            <p>Please log in to access the scanner.</p>
            <Link to="/?login=true" className="btn">
              Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const validationMessage = getValidationMessage();

  return (
    <div className="scanner-page">
      {/* Header */}
      <section className="scanner-header">
        <div className="container">
          <div className="scanner-header-content">
            <Link to="/profile" className="scanner-back">
              <ArrowLeft size={20} />
              Back
            </Link>
            <div className="scanner-header-main">
              <QrCode size={48} className="scanner-header-icon" />
              <h1>Ticket Scanner</h1>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="scanner-content section">
        <div className="container">
          <div className="scanner-container">
            {/* Camera View */}
            {scanning && (
              <div className="scanner-camera-view">
                <video
                  ref={videoRef}
                  className="scanner-video"
                  autoPlay
                  playsInline
                  muted
                />
                <div className="scanner-overlay">
                  <div className="scanner-scan-box"></div>
                  <p className="scanner-instruction">
                    Position QR code within the frame
                  </p>
                </div>
                <button
                  onClick={stopCamera}
                  className="scanner-stop-btn"
                >
                  Stop Scanning
                </button>
              </div>
            )}

            {/* Manual Input */}
            {!scanning && !validationResult && (
              <div className="scanner-manual-input">
                <div className="scanner-manual-header">
                  <Camera size={32} />
                  <h2>Scan Ticket QR Code</h2>
                  <p>Use your device camera or enter QR code data manually</p>
                </div>
                
                {cameraPermission === 'denied' && (
                  <div className="scanner-permission-warning">
                    <AlertCircle size={24} />
                    <p>Camera permission denied. Please enable camera access in your browser settings.</p>
                  </div>
                )}

                <div className="scanner-actions">
                  {cameraPermission === 'granted' && (
                    <button
                      onClick={startCamera}
                      className="btn btn-orange btn-large"
                    >
                      <Camera size={20} />
                      Start Camera Scanner
                    </button>
                  )}
                  
                  <div className="scanner-divider">
                    <span>OR</span>
                  </div>
                  
                  <div className="scanner-manual-form">
                    <label htmlFor="qr-input">Enter QR Code Data:</label>
                    <textarea
                      id="qr-input"
                      placeholder="Paste QR code data here (e.g., ticket:123 or JSON data)"
                      rows="4"
                      className="scanner-qr-input"
                    />
                    <button
                      onClick={() => {
                        const input = document.getElementById('qr-input');
                        if (input && input.value) {
                          handleManualInput(input.value);
                        }
                      }}
                      className="btn btn-block"
                      disabled={validating}
                    >
                      {validating ? 'Validating...' : 'Validate Ticket'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Validation Result */}
            {validationResult && validationMessage && (
              <div className={`scanner-result scanner-result-${validationMessage.type}`}>
                <div className="scanner-result-icon">
                  {validationMessage.icon}
                </div>
                <h2>{validationMessage.title}</h2>
                <p>{validationMessage.message}</p>
                {validationMessage.details}
                <div className="scanner-result-actions">
                  <button
                    onClick={handleScanAgain}
                    className="btn btn-orange"
                  >
                    Scan Another Ticket
                  </button>
                  <Link to="/profile" className="btn btn-secondary">
                    Back to Profile
                  </Link>
                </div>
              </div>
            )}

            {/* Error Display */}
            {error && !validationResult && (
              <div className="scanner-error">
                <XCircle size={32} />
                <p>{error}</p>
                <button
                  onClick={() => {
                    setError(null);
                    if (!scanning) startCamera();
                  }}
                  className="btn"
                >
                  Try Again
                </button>
              </div>
            )}

            {/* Loading State */}
            {validating && (
              <div className="scanner-loading">
                <div className="scanner-spinner"></div>
                <p>Validating ticket...</p>
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="scanner-instructions">
            <h3>How to Use</h3>
            <ol>
              <li>Click "Start Camera Scanner" to use your device camera</li>
              <li>Point the camera at the ticket QR code</li>
              <li>The ticket will be automatically validated</li>
              <li>Or manually enter QR code data if camera is unavailable</li>
            </ol>
            <div className="scanner-note">
              <strong>Note:</strong> In production, this will use the html5-qrcode library for automatic QR code detection.
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Scanner;


